// Sensor Panel Component - Controls for temperature/humidity sensors

import React from 'react';
import styled from 'styled-components';
import { TEST_SENSORS } from '../constants';
import type { SensorStates, SensorState } from '../types';

const Panel = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
`;

const PanelTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 500;
  color: #00bcd4;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SensorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
`;

const SensorCard = styled.div<{ $enabled: boolean; $color: string }>`
  background: ${props => (props.$enabled ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.02)')};
  border-radius: 6px;
  padding: 0.75rem;
  border-left: 3px solid ${props => props.$color};
  opacity: ${props => (props.$enabled ? 1 : 0.5)};
  transition: all 0.2s;
`;

const SensorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const SensorLabel = styled.span`
  font-weight: 500;
  font-size: 0.9rem;
`;

const SensorId = styled.span`
  font-size: 0.7rem;
  opacity: 0.6;
  font-family: monospace;
`;

const SensorModel = styled.span<{ $model: string }>`
  font-size: 0.65rem;
  padding: 0.15rem 0.4rem;
  border-radius: 3px;
  margin-left: 0.5rem;
  background: ${props =>
    props.$model === 'FancyWhite'
      ? 'rgba(255, 87, 34, 0.2)'
      : props.$model === 'MST01V2'
        ? 'rgba(76, 175, 80, 0.2)'
        : 'rgba(0, 188, 212, 0.2)'};
  color: ${props => (props.$model === 'FancyWhite' ? '#FF5722' : props.$model === 'MST01V2' ? '#4CAF50' : '#00BCD4')};
`;

const NoHumidityBadge = styled.span`
  font-size: 0.6rem;
  padding: 0.1rem 0.3rem;
  border-radius: 2px;
  margin-left: 0.3rem;
  background: rgba(255, 152, 0, 0.2);
  color: #ff9800;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 22px;
    transition: 0.2s;

    &:before {
      position: absolute;
      content: '';
      height: 16px;
      width: 16px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      border-radius: 50%;
      transition: 0.2s;
    }
  }

  input:checked + span {
    background-color: #4caf50;
  }

  input:checked + span:before {
    transform: translateX(18px);
  }
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ControlLabel = styled.span`
  font-size: 0.8rem;
  opacity: 0.8;
  min-width: 80px;
`;

const ValueDisplay = styled.span<{ $warning?: boolean }>`
  font-family: monospace;
  font-size: 0.9rem;
  color: ${props => (props.$warning ? '#ff9800' : '#4caf50')};
  min-width: 60px;
  text-align: right;
`;

const Slider = styled.input`
  flex: 1;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  outline: none;
  -webkit-appearance: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    background: #00bcd4;
    border-radius: 50%;
    cursor: pointer;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface SensorPanelProps {
  sensorStates: SensorStates;
  onUpdateSensor: (sensorId: string, updates: Partial<SensorState>) => void;
}

export const SensorPanel: React.FC<SensorPanelProps> = ({ sensorStates, onUpdateSensor }) => {
  const enabledCount = Object.values(sensorStates).filter(s => s.enabled).length;

  return (
    <Panel>
      <PanelTitle>
        <span className="material-icons" style={{ fontSize: '1.2rem' }}>
          thermostat
        </span>
        Temperature & Humidity Sensors ({enabledCount}/{TEST_SENSORS.length} active)
        <span style={{ fontSize: '0.75rem', opacity: 0.7, marginLeft: '1rem', fontWeight: 'normal' }}>
          Click "Send Once" or "Start Simulation" to send sensor data. New sensors appear in /sensors after ~2 min.
        </span>
      </PanelTitle>

      <SensorGrid>
        {TEST_SENSORS.map(sensor => {
          const state = sensorStates[sensor.id] || {
            temperature: sensor.defaultTemperature,
            humidity: sensor.defaultHumidity,
            enabled: true,
          };

          return (
            <SensorCard key={sensor.id} $enabled={state.enabled} $color={sensor.color}>
              <SensorHeader>
                <div>
                  <SensorLabel>
                    {sensor.label}
                    <SensorModel $model={sensor.model}>{sensor.model}</SensorModel>
                    {!sensor.hasHumidity && <NoHumidityBadge>No Humidity</NoHumidityBadge>}
                  </SensorLabel>
                  <br />
                  <SensorId>
                    {sensor.id} | FW: {sensor.firmwareVersion}
                  </SensorId>
                </div>
                <ToggleSwitch>
                  <input
                    type="checkbox"
                    checked={state.enabled}
                    onChange={e => onUpdateSensor(sensor.id, { enabled: e.target.checked })}
                  />
                  <span />
                </ToggleSwitch>
              </SensorHeader>

              <ControlRow>
                <ControlLabel>Temperature</ControlLabel>
                <Slider
                  type="range"
                  min={-10}
                  max={150}
                  value={state.temperature}
                  disabled={!state.enabled}
                  onChange={e => onUpdateSensor(sensor.id, { temperature: Number(e.target.value) })}
                />
                <ValueDisplay $warning={state.temperature > 100 || state.temperature < 32}>
                  {state.temperature.toFixed(1)}°F
                </ValueDisplay>
              </ControlRow>

              {sensor.hasHumidity && (
                <ControlRow>
                  <ControlLabel>Humidity</ControlLabel>
                  <Slider
                    type="range"
                    min={0}
                    max={100}
                    value={state.humidity}
                    disabled={!state.enabled}
                    onChange={e => onUpdateSensor(sensor.id, { humidity: Number(e.target.value) })}
                  />
                  <ValueDisplay $warning={state.humidity > 80 || state.humidity < 20}>
                    {state.humidity.toFixed(1)}%
                  </ValueDisplay>
                </ControlRow>
              )}
            </SensorCard>
          );
        })}
      </SensorGrid>
    </Panel>
  );
};

export default SensorPanel;
